'use client'

import { dialog } from '@/modules/ui/dialog'
import { motion } from 'motion/react'
import { PropsWithChildren, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence } from 'motion/react'

export interface DialogProps {
  title?: string
  message?: string
  confirmMessage?: string
  cancelMessage?: string
  onClickConfirm: () => void
  onClickCancel: () => void
  cancelVisible?: boolean
}

export const Dialog = ({
  title,
  message,
  onClickConfirm,
  onClickCancel,
  cancelVisible = false,
  confirmMessage = '확인',
  cancelMessage = '취소',
}: DialogProps) => {
  const handleClickCancelButton = () => {
    onClickCancel()
    dialog.cancel()
  }

  const handleClickConfirmButton = () => {
    onClickConfirm()
    dialog.cancel()
  }

  return (
    <DialogPortal>
      <AnimatePresence>
        <motion.div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            layout
            className="flex flex-col w-[calc(100%-80px)] max-w-[312px] m-h-[280px] py-[24px] px-[20px] bg-white"
          >
            {title && <p className="text-body1_strong text-black mb-[16px]">{title}</p>}
            {message && <p className="text-body2_strong text-gray-6 whitespace-pre-line">{message}</p>}
            <div className="flex mt-[20px] justify-end w-[100%] gap-[20px]">
              {cancelVisible && (
                <button className="text-body1_strong text-gray-3 cursor-pointer" onClick={handleClickCancelButton}>
                  {cancelMessage}
                </button>
              )}
              <button className="text-body1_strong text-pink-1 cursor-pointer" onClick={handleClickConfirmButton}>
                {confirmMessage}
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </DialogPortal>
  )
}

export default Dialog

const DialogPortal = ({ children }: PropsWithChildren) => {
  const dialogRoot = document.getElementById('dialog-root')
  const el = document.createElement('div')

  useEffect(() => {
    if (typeof window === 'undefined') return

    dialogRoot?.appendChild(el)

    return () => {
      dialogRoot?.removeChild(el)
    }
  }, [el, dialogRoot])

  if (!el) return null

  return createPortal(children, el)
}
